<template>
  <div class="space-y-6">
    <div
      class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white">
          {{ $t("leads") }}
        </h1>
        <p class="text-sm text-gray-500 dark:text-slate-400">
          {{ $t("lead_management_subtitle") }}
        </p>
      </div>
      <div class="flex gap-2">
        <BaseButton type="secondary" @click="fetchLeads">{{
          $t("refresh_data")
        }}</BaseButton>
        <BaseButton type="primary" @click="openCreate"
          >+ {{ $t("add_lead") }}</BaseButton
        >
      </div>
    </div>

    <div class="rounded-lg bg-white p-4 shadow-sm dark:bg-dark-surface">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <label
          class="block text-sm font-medium text-gray-700 dark:text-slate-300"
        >
          {{ $t("search_leads") }}
          <SearchBar
            v-model="filters.search"
            class="mt-1"
            :placeholder="$t('search_leads') + '...'"
            @search="applyFilters"
          />
        </label>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-slate-300"
        >
          {{ $t("source") }}
          <input
            v-model.trim="filters.source"
            class="field mt-1"
            placeholder="WhatsApp, Facebook..."
            @keyup.enter="applyFilters"
          />
        </label>
        <label
          v-if="isAdmin"
          class="block text-sm font-medium text-gray-700 dark:text-slate-300"
        >
          {{ $t("moderator") }}
          <BaseSelect
            v-model="filters.moderator_id"
            class="mt-1"
            @change="applyFilters"
          >
            <option value="">{{ $t("all_moderators") }}</option>
            <option
              v-for="moderator in moderators"
              :key="moderator.id"
              :value="moderator.id"
            >
              {{ moderatorName(moderator) }}
            </option>
          </BaseSelect>
        </label>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-slate-300"
        >
          {{ $t("per_page") }}
          <BaseSelect
            v-model="filters.per_page"
            class="mt-1"
            @change="applyFilters"
          >
            <option :value="15">15</option>
            <option :value="30">30</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </BaseSelect>
        </label>
      </div>

      <div class="mt-4 flex flex-wrap gap-2">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="rounded-full px-3 py-1.5 text-sm font-medium transition"
          :class="
            activeTab === tab.key
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-dark-input dark:text-slate-300'
          "
          @click="selectTab(tab.key)"
        >
          {{ tab.label }}
        </button>
        <button
          class="ml-auto text-sm font-medium text-blue-600 hover:text-blue-800"
          @click="resetFilters"
        >
          {{ $t("reset_filters") }}
        </button>
      </div>
    </div>

    <p v-if="loading" class="text-sm text-gray-500">{{ $t("loading") }}</p>

    <BaseDataTable :columns="columns" :data="leads">
      <template #name="{ row }">
        <div class="font-semibold text-gray-900 dark:text-white">
          {{ row.name }}
        </div>
        <div class="text-xs text-gray-500">{{ row.phone }}</div>
      </template>
      <template #status="{ row }">
        <span
          class="rounded-full px-2.5 py-1 text-xs font-semibold"
          :class="statusClass(row.status)"
        >
          {{ formatStatus(row.status) }}
        </span>
      </template>
      <template #car="{ row }">
        {{
          row.car ? row.car.name || row.car.name_ar || `#${row.car.id}` : "—"
        }}
      </template>
      <template #source="{ row }">{{ row.source || "—" }}</template>
      <template #moderator="{ row }">{{ row.moderator?.name || "—" }}</template>
      <template #latest_update="{ row }">
        <div v-if="row.latest_update" class="max-w-xs">
          <p class="truncate">{{ row.latest_update.comment }}</p>
          <p class="text-xs text-gray-500">
            {{ row.latest_update.updated_by?.name || "—" }} ·
            {{ formatDate(row.latest_update.created_at) }}
          </p>
        </div>
        <span v-else>—</span>
      </template>
      <template #follow_up="{ row }">
        <div
          :class="
            isDue(row.next_follow_up_at) ? 'font-semibold text-red-600' : ''
          "
        >
          {{ formatDate(row.next_follow_up_at) }}
        </div>
        <div class="text-xs text-gray-500">
          {{ $t("last_contacted") }}: {{ formatDate(row.last_contacted_at) }}
        </div>
      </template>
      <template #updates_count="{ row }">{{ row.updates_count || 0 }}</template>
      <template #actions="{ row }">
        <div class="flex gap-3 whitespace-nowrap font-medium">
          <button
            class="text-blue-600 hover:text-blue-800"
            @click="openDetails(row.id)"
          >
            {{ $t("view") }}
          </button>
          <button
            class="text-amber-600 hover:text-amber-800"
            @click="openEdit(row)"
          >
            {{ $t("edit") }}
          </button>
          <button
            class="text-emerald-600 hover:text-emerald-800"
            @click="openUpdate(row.id)"
          >
            {{ $t("add_update") }}
          </button>
        </div>
      </template>
    </BaseDataTable>

    <BasePagination
      v-if="pagination"
      :pagination="pagination"
      @change-page="changePage"
    />

    <div v-if="showLeadForm" class="modal-backdrop" @click.self="closeLeadForm">
      <form class="modal-card" @submit.prevent="saveLead">
        <div class="flex items-center justify-between gap-4">
          <h2 class="text-xl font-bold text-gray-900">
            {{ leadFormMode === "create" ? $t("add_lead") : $t("edit_lead") }}
          </h2>
          <button
            type="button"
            class="text-2xl text-gray-400 hover:text-gray-700"
            @click="closeLeadForm"
          >
            ×
          </button>
        </div>
        <div class="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <BaseInput
            v-model="leadForm.name"
            :label="$t('lead_name')"
            required
          />
          <BaseInput v-model="leadForm.phone" :label="$t('phone')" required />
          <BaseInput v-model="leadForm.source" :label="$t('source')" />
          <BaseInput
            v-model="leadForm.next_follow_up_at"
            :label="$t('next_follow_up')"
            type="datetime-local"
          />
          <label
            v-if="leadFormMode === 'create'"
            class="block text-sm font-medium text-gray-700"
          >
            {{ $t("initial_status") }}
            <select v-model="leadForm.status" class="field mt-1" required>
              <option v-for="status in statuses" :key="status" :value="status">
                {{ formatStatus(status) }}
              </option>
            </select>
          </label>
          <label v-if="isAdmin" class="block text-sm font-medium text-gray-700">
            {{ $t("assigned_moderator") }}
            <select
              v-model="leadForm.moderator_id"
              class="field mt-1"
              :required="leadFormMode === 'create'"
            >
              <option value="">{{ $t("select_moderator") }}</option>
              <option
                v-for="moderator in moderators"
                :key="moderator.id"
                :value="moderator.id"
              >
                {{ moderatorName(moderator) }}
              </option>
            </select>
          </label>
        </div>
        <label
          v-if="leadFormMode === 'create'"
          class="mt-4 block text-sm font-medium text-gray-700"
        >
          {{ $t("initial_comment") }}
          <span class="font-normal text-gray-400">({{ $t("optional") }})</span>
          <textarea
            v-model.trim="leadForm.comment"
            class="field mt-1 min-h-24"
          />
        </label>
        <BaseInput
          v-if="leadFormMode === 'create' && leadForm.status === 'lost'"
          v-model="leadForm.lost_reason"
          class="mt-4"
          :label="$t('lost_reason')"
          required
        />
        <p v-if="formError" class="mt-4 text-sm text-red-600">
          {{ formError }}
        </p>
        <div class="mt-6 flex justify-end gap-3">
          <BaseButton type="secondary" @click="closeLeadForm">{{
            $t("cancel")
          }}</BaseButton>
          <BaseButton type="primary" button-type="submit">{{
            saving ? $t("saving_changes") : $t("save")
          }}</BaseButton>
        </div>
      </form>
    </div>

    <div v-if="selectedLead" class="modal-backdrop" @click.self="closeDetails">
      <div class="modal-card modal-card--wide">
        <div class="flex items-center justify-between gap-4">
          <div>
            <h2 class="text-xl font-bold text-gray-900">
              {{ selectedLead.name }}
            </h2>
            <p class="text-sm text-gray-500">{{ selectedLead.phone }}</p>
          </div>
          <button
            class="text-2xl text-gray-400 hover:text-gray-700"
            @click="closeDetails"
          >
            ×
          </button>
        </div>
        <div
          class="mt-5 grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-4 text-sm md:grid-cols-4"
        >
          <div>
            <p class="label">{{ $t("status") }}</p>
            <p>{{ formatStatus(selectedLead.status) }}</p>
          </div>
          <div>
            <p class="label">{{ $t("source") }}</p>
            <p>{{ selectedLead.source || "—" }}</p>
          </div>
          <div>
            <p class="label">{{ $t("car") }}</p>
            <p>
              {{
                selectedLead.car
                  ? selectedLead.car.name ||
                    selectedLead.car.name_ar ||
                    `#${selectedLead.car.id}`
                  : "—"
              }}
            </p>
          </div>
          <div>
            <p class="label">{{ $t("moderator") }}</p>
            <p>{{ selectedLead.moderator?.name || "—" }}</p>
          </div>
          <div>
            <p class="label">{{ $t("next_follow_up") }}</p>
            <p>{{ formatDate(selectedLead.next_follow_up_at) }}</p>
          </div>
          <div v-if="selectedLead.converted_user" class="md:col-span-2">
            <p class="label">{{ $t("converted_user") }}</p>
            <p>
              {{
                selectedLead.converted_user.name ||
                selectedLead.converted_user.full_name_en ||
                "—"
              }}
            </p>
          </div>
        </div>

        <form
          v-if="showUpdateForm"
          class="mt-6 rounded-lg border border-blue-100 bg-blue-50 p-4"
          @submit.prevent="submitUpdate"
        >
          <h3 class="font-semibold text-gray-900">
            {{ $t("add_timeline_update") }}
          </h3>
          <textarea
            v-model.trim="updateForm.comment"
            class="field mt-3 min-h-24"
            :placeholder="$t('required_comment')"
            required
          />
          <div class="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t("new_status") }}
              <span class="font-normal text-gray-400"
                >({{ $t("optional") }})</span
              >
              <select v-model="updateForm.status" class="field mt-1">
                <option value="">{{ $t("keep_current_status") }}</option>
                <option
                  v-for="status in statuses"
                  :key="status"
                  :value="status"
                >
                  {{ formatStatus(status) }}
                </option>
              </select>
            </label>
            <BaseInput
              v-model="updateForm.next_follow_up_at"
              :label="$t('next_follow_up') + ' (' + $t('optional') + ')'"
              type="datetime-local"
            />
          </div>
          <BaseInput
            v-if="updateForm.status === 'lost'"
            v-model="updateForm.lost_reason"
            class="mt-3"
            :label="$t('lost_reason')"
            required
          />
          <p v-if="updateError" class="mt-3 text-sm text-red-600">
            {{ updateError }}
          </p>
          <div class="mt-4 text-right">
            <BaseButton type="primary" button-type="submit">{{
              updating ? $t("adding_update") : $t("add_update")
            }}</BaseButton>
          </div>
        </form>

        <div class="mt-6">
          <div class="flex items-center justify-between">
            <h3 class="font-semibold text-gray-900">{{ $t("timeline") }}</h3>
            <span class="text-sm text-gray-500">{{
              $t("lead_updates_count", {
                count: selectedLead.updates_count || updates.length,
              })
            }}</span>
          </div>
          <p v-if="updatesLoading" class="mt-3 text-sm text-gray-500">
            {{ $t("loading_timeline") }}
          </p>
          <div
            v-else-if="!updates.length"
            class="mt-3 rounded-lg border border-dashed p-4 text-sm text-gray-500"
          >
            {{ $t("no_timeline_updates") }}
          </div>
          <ol v-else class="mt-4 space-y-4 border-l-2 border-gray-200 pl-5">
            <li v-for="update in updates" :key="update.id" class="relative">
              <span
                class="absolute -left-7 top-1 h-3 w-3 rounded-full bg-blue-500"
              ></span>
              <p class="text-sm text-gray-800">{{ update.comment }}</p>
              <p class="mt-1 text-xs text-gray-500">
                {{ update.updated_by?.name || "—" }} ·
                {{ formatDate(update.created_at) }}
                <span v-if="update.status_before || update.status_after">
                  · {{ formatStatus(update.status_before) }} →
                  {{ formatStatus(update.status_after) }}</span
                >
              </p>
            </li>
          </ol>
          <button
            v-if="hasMoreUpdates"
            class="mt-4 text-sm font-semibold text-blue-600 hover:text-blue-800"
            :disabled="updatesLoading"
            @click="loadMoreUpdates"
          >
            {{ updatesLoading ? $t("loading") : $t("load_more_updates") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/components/Button.vue";
import BaseDataTable from "@/components/BaseDataTable.vue";
import BasePagination from "@/components/BasePagination.vue";
import BaseInput from "@/components/BaseInput.vue";
import BaseSelect from "@/components/BaseSelect.vue";
import SearchBar from "@/components/SearchBar.vue";
import activityLogService from "@/services/activityLog.service";
import leadService from "@/services/lead.service";
import Swal from "sweetalert2";

const emptyLeadForm = () => ({
  name: "",
  phone: "",
  source: "",
  next_follow_up_at: "",
  status: "new",
  moderator_id: "",
  comment: "",
  lost_reason: "",
});
const emptyUpdateForm = () => ({
  comment: "",
  status: "",
  next_follow_up_at: "",
  lost_reason: "",
});

export default {
  name: "LeadsList",
  components: {
    BaseButton,
    BaseDataTable,
    BasePagination,
    BaseInput,
    BaseSelect,
    SearchBar,
  },
  data() {
    return {
      leads: [],
      columns: [],
      pagination: null,
      moderators: [],
      loading: false,
      saving: false,
      updating: false,
      updatesLoading: false,
      selectedLead: null,
      updates: [],
      updatesPagination: null,
      showLeadForm: false,
      showUpdateForm: false,
      leadFormMode: "create",
      leadForm: emptyLeadForm(),
      updateForm: emptyUpdateForm(),
      formError: "",
      updateError: "",
      activeTab: "all",
      filters: {
        search: "",
        status: "",
        source: "",
        moderator_id: "",
        follow_up_before: "",
        page: 1,
        per_page: 15,
      },
      statuses: [
        "new",
        "contacted",
        "follow_up",
        "qualified",
        "converted",
        "not_interested",
        "unreachable",
        "lost",
      ],
    };
  },
  computed: {
    isAdmin() {
      return this.currentUser?.roles?.includes("admin");
    },
    currentUser() {
      try {
        return JSON.parse(localStorage.getItem("admin_user") || "{}");
      } catch {
        return {};
      }
    },
    tabs() {
      return [
        { key: "all", label: this.$t("all") },
        ...this.statuses.map((status) => ({
          key: status,
          label: this.formatStatus(status),
        })),
        { key: "due", label: this.$t("lead_due_overdue") },
      ];
    },
    hasMoreUpdates() {
      return (
        this.updatesPagination &&
        this.updatesPagination.current_page < this.updatesPagination.total_pages
      );
    },
  },
  watch: {
    "$i18n.locale": {
      immediate: true,
      handler() {
        this.updateColumns();
      },
    },
  },
  async mounted() {
    await Promise.all([
      this.fetchLeads(),
      this.isAdmin ? this.fetchModerators() : Promise.resolve(),
    ]);
  },
  methods: {
    updateColumns() {
      const columns = [
        { label: this.$t("lead"), field: "name" },
        { label: this.$t("status"), field: "status" },
        { label: this.$t("car"), field: "car" },
        { label: this.$t("source"), field: "source" },
      ];
      if (this.isAdmin)
        columns.push({ label: this.$t("moderator"), field: "moderator" });
      this.columns = [
        ...columns,
        { label: this.$t("latest_update"), field: "latest_update" },
        { label: this.$t("follow_up_contacted"), field: "follow_up" },
        { label: this.$t("updates"), field: "updates_count" },
      ];
    },
    async fetchLeads() {
      this.loading = true;
      try {
        const response = await leadService.list(this.cleanParams(this.filters));
        this.leads = response.data?.data || [];
        this.pagination = response.data?.pagination || null;
      } catch (error) {
        this.showError(error, "failed_fetch_leads");
      } finally {
        this.loading = false;
      }
    },
    async fetchModerators() {
      try {
        const response = await activityLogService.getModerators();
        this.moderators = response.data?.data || [];
      } catch (error) {
        this.showError(error, "failed_fetch_moderators");
      }
    },
    cleanParams(params) {
      return Object.fromEntries(
        Object.entries(params).filter(
          ([, value]) => value !== "" && value !== null && value !== undefined
        )
      );
    },
    applyFilters() {
      this.filters.page = 1;
      this.fetchLeads();
    },
    resetFilters() {
      this.activeTab = "all";
      this.filters = {
        search: "",
        status: "",
        source: "",
        moderator_id: "",
        follow_up_before: "",
        page: 1,
        per_page: 15,
      };
      this.fetchLeads();
    },
    selectTab(tab) {
      this.activeTab = tab;
      this.filters.page = 1;
      this.filters.status = tab === "all" || tab === "due" ? "" : tab;
      this.filters.follow_up_before =
        tab === "due" ? this.apiDate(new Date()) : "";
      this.fetchLeads();
    },
    changePage(page) {
      this.filters.page = page;
      this.fetchLeads();
    },
    openCreate() {
      this.leadFormMode = "create";
      this.leadForm = emptyLeadForm();
      this.formError = "";
      this.showLeadForm = true;
    },
    openEdit(lead) {
      this.leadFormMode = "edit";
      this.formError = "";
      this.leadForm = {
        ...emptyLeadForm(),
        id: lead.id,
        name: lead.name,
        phone: lead.phone,
        source: lead.source || "",
        next_follow_up_at: this.inputDate(lead.next_follow_up_at),
        moderator_id: lead.moderator?.id || "",
      };
      this.showLeadForm = true;
    },
    closeLeadForm() {
      if (!this.saving) this.showLeadForm = false;
    },
    async saveLead() {
      this.formError = "";
      if (
        this.leadFormMode === "create" &&
        this.isAdmin &&
        !this.leadForm.moderator_id
      )
        return (this.formError = this.$t("required_assigned_moderator"));
      if (
        this.leadFormMode === "create" &&
        this.leadForm.status === "lost" &&
        !this.leadForm.lost_reason
      )
        return (this.formError = this.$t("required_lost_reason"));
      this.saving = true;
      try {
        const payload =
          this.leadFormMode === "create"
            ? this.createPayload()
            : this.editPayload();
        if (this.leadFormMode === "create") await leadService.create(payload);
        else await leadService.update(this.leadForm.id, payload);
        this.showLeadForm = false;
        await this.fetchLeads();
        Swal.fire(
          this.$t("success"),
          this.$t(
            this.leadFormMode === "create"
              ? "lead_created_success"
              : "lead_updated_success"
          ),
          "success"
        );
      } catch (error) {
        this.formError = this.errorMessage(error);
      } finally {
        this.saving = false;
      }
    },
    createPayload() {
      const payload = {
        name: this.leadForm.name,
        phone: this.leadForm.phone,
        status: this.leadForm.status,
      };
      ["source", "comment", "lost_reason"].forEach((key) => {
        if (this.leadForm[key]) payload[key] = this.leadForm[key];
      });
      if (this.leadForm.next_follow_up_at)
        payload.next_follow_up_at = this.apiDate(
          this.leadForm.next_follow_up_at
        );
      if (this.isAdmin) payload.moderator_id = this.leadForm.moderator_id;
      return payload;
    },
    editPayload() {
      const payload = {
        name: this.leadForm.name,
        phone: this.leadForm.phone,
        source: this.leadForm.source || null,
        next_follow_up_at: this.leadForm.next_follow_up_at
          ? this.apiDate(this.leadForm.next_follow_up_at)
          : null,
      };
      if (this.isAdmin && this.leadForm.moderator_id)
        payload.moderator_id = this.leadForm.moderator_id;
      return payload;
    },
    async openDetails(id) {
      this.selectedLead = null;
      this.showUpdateForm = false;
      this.updates = [];
      this.updatesPagination = null;
      this.updatesLoading = true;
      try {
        const [leadResponse, updatesResponse] = await Promise.all([
          leadService.get(id),
          leadService.getUpdates(id, { per_page: 100 }),
        ]);
        this.selectedLead = leadResponse.data?.data;
        this.updates = updatesResponse.data?.data || [];
        this.updatesPagination = updatesResponse.data?.pagination || null;
      } catch (error) {
        this.showError(error, "failed_load_lead_details");
      } finally {
        this.updatesLoading = false;
      }
    },
    async loadMoreUpdates() {
      if (!this.selectedLead || !this.hasMoreUpdates) return;
      this.updatesLoading = true;
      try {
        const response = await leadService.getUpdates(this.selectedLead.id, {
          per_page: 100,
          page: this.updatesPagination.current_page + 1,
        });
        this.updates.push(...(response.data?.data || []));
        this.updatesPagination =
          response.data?.pagination || this.updatesPagination;
      } catch (error) {
        this.showError(error, "failed_load_more_updates");
      } finally {
        this.updatesLoading = false;
      }
    },
    async openUpdate(id) {
      await this.openDetails(id);
      if (this.selectedLead) this.showUpdateForm = true;
      this.updateForm = emptyUpdateForm();
      this.updateError = "";
    },
    closeDetails() {
      if (!this.updating) {
        this.selectedLead = null;
        this.showUpdateForm = false;
      }
    },
    async submitUpdate() {
      this.updateError = "";
      if (this.updateForm.status === "lost" && !this.updateForm.lost_reason)
        return (this.updateError = this.$t("required_lost_reason"));
      this.updating = true;
      try {
        const payload = { comment: this.updateForm.comment };
        if (this.updateForm.status) payload.status = this.updateForm.status;
        if (this.updateForm.next_follow_up_at)
          payload.next_follow_up_at = this.apiDate(
            this.updateForm.next_follow_up_at
          );
        if (this.updateForm.status === "lost")
          payload.lost_reason = this.updateForm.lost_reason;
        await leadService.addUpdate(this.selectedLead.id, payload);
        await this.fetchLeads();
        this.updateForm = emptyUpdateForm();
        this.selectedLead = null;
        this.showUpdateForm = false;
        Swal.fire(
          this.$t("success"),
          this.$t("lead_update_created_success"),
          "success"
        );
      } catch (error) {
        this.updateError = this.errorMessage(error);
      } finally {
        this.updating = false;
      }
    },
    moderatorName(moderator) {
      return (
        moderator.name ||
        moderator.full_name_en ||
        moderator.username ||
        `${this.$t("moderator")} #${moderator.id}`
      );
    },
    formatStatus(status) {
      return status ? this.$t(`lead_status_${status}`) : "—";
    },
    statusClass(status) {
      return (
        {
          new: "bg-slate-100 text-slate-700",
          contacted: "bg-blue-100 text-blue-700",
          follow_up: "bg-violet-100 text-violet-700",
          qualified: "bg-emerald-100 text-emerald-700",
          converted: "bg-green-100 text-green-700",
          not_interested: "bg-orange-100 text-orange-700",
          unreachable: "bg-amber-100 text-amber-700",
          lost: "bg-red-100 text-red-700",
        }[status] || "bg-gray-100 text-gray-700"
      );
    },
    formatDate(value) {
      return value
        ? new Date(value).toLocaleString(
            this.$i18n.locale === "ar" ? "ar-EG" : "en-US",
            { dateStyle: "medium", timeStyle: "short" }
          )
        : "—";
    },
    inputDate(value) {
      if (!value) return "";
      const date = new Date(value);
      const pad = (number) => String(number).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    apiDate(value) {
      if (value instanceof Date) {
        const pad = (number) => String(number).padStart(2, "0");
        return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(
          value.getDate()
        )} ${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(
          value.getSeconds()
        )}`;
      }
      const date = String(value);
      return date.replace("T", " ") + (date.length === 16 ? ":00" : "");
    },
    isDue(value) {
      return value && new Date(value) <= new Date();
    },
    errorMessage(error, fallbackKey = "request_failed") {
      const data = error.response?.data;
      const validationErrors = Object.values(data?.errors || {})
        .flat()
        .join(" ");
      return validationErrors || data?.message || this.$t(fallbackKey);
    },
    showError(error, fallbackKey) {
      Swal.fire(
        this.$t("error"),
        this.errorMessage(error, fallbackKey),
        "error"
      );
    },
  },
};
</script>

<style scoped>
.field {
  @apply w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100;
}
.modal-backdrop {
  @apply fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-900 bg-opacity-50 p-4;
}
.modal-card {
  @apply my-auto w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl;
}
.modal-card--wide {
  @apply max-w-4xl;
}
.label {
  @apply text-xs font-semibold uppercase tracking-wide text-gray-500;
}
</style>
